import React, { useEffect } from 'react';
import { Box, Modal, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useTeleportModal } from './context/context';
import { useOverlayContext } from '@providers/OverlayProvider';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { ITeleportPreview } from '@interfaces/map';
import { AnalyticsType } from '@interfaces/analytics';
import { MytaverseLogger } from '@helpers/logger';
import PulsingBorder from '@components/PulsingBorder';
import { TutorialHighlightKey, useTutorialHighlight } from '@core/tutorials';
import {
  getTeleportTitle,
  getTeleportTooltip,
  resolveTeleportLocationId,
} from './teleportHelpers';
import { useTeleportOpen, useTeleportPreviews } from './hooks/useTeleportOpen';

import { teleportOpenGuard } from './teleportOpenGuard';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';

const TeleportModal = () => {
  const { t: translate } = useTranslation();
  const classes = useStyles();
  const { open, setOpen } = useTeleportModal();
  const { teleportPreviews } = useTeleportPreviews();
  const { closeTeleport } = useTeleportOpen();
  const { trackEventAnalytics, setIsTeleportingToRoomByUnreal } =
    useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { hideCurrentlyOpenOverlays } = useOverlayContext();
  const isFirstLocationHighlighted = useTutorialHighlight(
    TutorialHighlightKey.TeleportFirstLocation,
  );

  useEffect(() => {
    if (
      !open ||
      !hideCurrentlyOpenOverlays ||
      teleportOpenGuard.suppressHideClose
    ) {
      return;
    }

    setOpen(false);
    setIsTeleportingToRoomByUnreal(false);
  }, [
    hideCurrentlyOpenOverlays,
    open,
    setOpen,
    setIsTeleportingToRoomByUnreal,
  ]);

  const handleTeleport = (tp: ITeleportPreview) => {
    const locationId = resolveTeleportLocationId(tp);
    MytaverseLogger.log(
      `Web teleport to location: ${tp.teleportId} (id: ${locationId})`,
    );

    sendMessageToUnreal({
      action: WebsocketAction.TeleportToIdLocation,
      id: locationId,
    });

    trackEventAnalytics(AnalyticsType.TeleportToRegion, {
      region: tp.teleportId,
    });

    setOpen(false);
    setIsTeleportingToRoomByUnreal(false);
  };

  if (teleportPreviews.length === 0) {
    return null;
  }

  return (
    <Modal sx={classes.root} open={open} hideBackdrop disableAutoFocus>
      <Box component="div" sx={classes.container}>
        <Box sx={classes.header}>
          <Typography sx={classes.title}>
            {translate('tooltips.teleport')}
          </Typography>
          <Box
            component="button"
            type="button"
            sx={classes.closeButton}
            onClick={closeTeleport}
            aria-label="Close teleport"
          >
            <CloseIcon sx={classes.closeIcon} />
          </Box>
        </Box>
        <Box sx={classes.scrollWrapper}>
          <Box sx={classes.grid}>
            {teleportPreviews.map((tp, index) => {
              const title = getTeleportTitle(tp);
              const tooltip = getTeleportTooltip(tp);
              const description = tp.description?.trim();
              const locationId = resolveTeleportLocationId(tp);
              const card = (
                <Box
                  sx={classes.card}
                  onClick={() => handleTeleport(tp)}
                  data-testid={`teleport-card-${index}`}
                >
                  {tp.previewImage?.url ? (
                    <Box
                      component="img"
                      src={tp.previewImage.url}
                      alt={title}
                      sx={classes.cardImage}
                    />
                  ) : (
                    <Box sx={classes.cardPlaceholder}>
                      <LocationOnIcon
                        sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 40 }}
                      />
                    </Box>
                  )}
                  <Typography sx={classes.cardLabel}>{title}</Typography>
                </Box>
              );
              return (
                <Tooltip
                  key={`${locationId}-${tp.teleportId}`}
                  title={tooltip}
                  placement="bottom"
                  arrow
                  disableHoverListener={!description}
                  PopperProps={{
                    modifiers: [
                      {
                        name: 'offset',
                        options: { offset: [0, 6] },
                      },
                      {
                        name: 'preventOverflow',
                        options: {
                          padding: { top: 8, right: 40, bottom: 8, left: 8 },
                        },
                      },
                    ],
                  }}
                >
                  {index === 0 ? (
                    <PulsingBorder
                      active={isFirstLocationHighlighted}
                      borderRadius="50%"
                    >
                      {card}
                    </PulsingBorder>
                  ) : (
                    card
                  )}
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default TeleportModal;
